import { useState } from 'react';
import { Box, Chip, Divider, Popover, Typography } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import ActivityHourlyGraph from './ActivityHourlyGraph';
import ActivityDistributions from './ActivityDistributions';
import { Framed, Row, Num, SectionTitle } from './activity-table';
import type { Theme } from '@mui/material/styles';

export interface ActivityWindow {
  visits: number;
  clicks: number;
  conversions: number;
}

export interface ActivityEntry {
  h1: ActivityWindow;
  h24: ActivityWindow;
  today: ActivityWindow;
  yesterday: ActivityWindow;
  age?: { avg: number; over65Pct: number; sampled: number };
  sources?: Array<{ id: string; name: string; visits: number }>;
  relay?: { sent: number; total: number };
  expected?: number;
  expectedCapped?: number;
}

// The joined columns are SELECT aliases, so their bare grid name emits `f.traffic_source`
// and errors; the dotted form is what mapToDBKey resolves. Absent = already a real column.
const ORDER_KEYS: Record<string, string> = {
  _activity: 'activity',
  traffic_source: 'traffic_source.name',
  network: 'network.name',
  postback: 'postback.name',
  partner: 'partner.name',
};

export const activityOrderBy = (sort: readonly { field: string }[]) => {
  const field = sort[0]?.field;
  return field ? (ORDER_KEYS[field] ?? field) : field;
};

const fmt = (n: number) => (n ?? 0).toLocaleString();

const compact = (n: number) =>
  Number(n ?? 0).toLocaleString('en', { maximumFractionDigits: 1, notation: 'compact' });

export type ActivityPart = 'expected' | 'h24' | 'h1';

const DEFAULT_PARTS: ActivityPart[] = ['h24', 'h1'];

/** Absent (not zero) without an expectation, so the part drops out of the label entirely. */
const partValue = (a: ActivityEntry, p: ActivityPart): number | undefined =>
  (p === 'expected' ? a.expected : a[p]?.visits);

/** Fitted to our traffic: prod rule groups top out near 11k, so 50k/10k stops paint one colour. */
const ACTIVITY_SCALE = [
  { min: 5000, color: '#29dd19' },
  { min: 500, color: '#aff56a' },
  { min: 1, color: '#f3f23f' },
  { min: 0, color: '#e7e7e7' },
];

export const ACTIVITY_CHIP_SX = (visits24h: number) => (theme: Theme) => {
  const stop = ACTIVITY_SCALE.find((s) => visits24h >= s.min) ?? ACTIVITY_SCALE[ACTIVITY_SCALE.length - 1];
  return {
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums' as const,
    border: 'none',
    bgcolor: stop.color,
    // Fixed hex in both themes, so the contrast has to be derived.
    color: theme.palette.getContrastText(stop.color),
  };
};

const WINDOWS: Array<[keyof Pick<ActivityEntry, 'h1' | 'h24' | 'today' | 'yesterday'>, string]> = [
  ['h1', 'Last hour'], ['h24', 'Last 24h'], ['today', 'Today (UTC)'], ['yesterday', 'Yesterday (UTC)'],
];

export default function ActivityCell({
  activity, senderLabel, parts = DEFAULT_PARTS, kind, entityId, updatedAt,
}: {
  activity?: ActivityEntry | null;
  senderLabel?: string;
  parts?: ActivityPart[];
  kind?: string;
  entityId?: string;
  updatedAt?: string | null;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const anything = activity && ((activity.expected ?? 0) > 0 || WINDOWS.some(([k]) => {
    const w = activity[k];
    return w && (w.visits > 0 || w.clicks > 0 || w.conversions > 0);
  }));
  if (!activity || !anything) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <Typography variant="body2" color="text.secondary">—</Typography>
      </Box>
    );
  }
  const { h24, age, sources = [], relay } = activity;
  const wide = Boolean(kind && entityId);

  return (
    <>
      <Box
        sx={{ display: 'flex', alignItems: 'center', height: '100%', cursor: 'pointer' }}
        onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); }}
      >
        <Chip
          size="small"
          // jsonb written by a job: a row missing a window must not take the chip down.
          sx={ACTIVITY_CHIP_SX(h24?.visits ?? 0)}
          label={parts
            .map((p) => partValue(activity, p))
            .filter((v): v is number => Number.isFinite(v))
            .map(compact)
            .join(' | ')}
        />
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              maxWidth: 960,
              p: 1.25,
              bgcolor: 'background.paper',
              color: 'text.primary',
              fontSize: 11,
              boxShadow: 10,
            },
          },
        }}
      >
        {(
        <Box sx={{ width: wide ? 920 : 'auto', maxWidth: '100%' }}>
          <Box sx={{
            display: 'flex',
            flexDirection: wide ? 'row' : 'column',
            alignItems: 'stretch',
            gap: wide ? 2 : 0.75,
          }}>
            {sources.length > 0 && (
              <Box sx={{ flex: wide ? '1.35 1 0' : '1 1 0', minWidth: 0 }}>
                <SectionTitle>{senderLabel || 'Sources'}</SectionTitle>
                <Framed cols="1fr 64px">
                  <Row head label="Source"><Num head>Visits</Num></Row>
                  {sources.map((s) => (
                    <Row key={s.id} label={s.name || s.id} title={s.name || s.id}>
                      <Num>{fmt(s.visits)}</Num>
                    </Row>
                  ))}
                </Framed>
              </Box>
            )}

            <Box sx={{ flex: wide ? '1.2 1 0' : '1 1 auto', minWidth: 0 }}>
              <SectionTitle>Traffic</SectionTitle>
              <Framed cols="1fr 60px 60px 60px">
                <Row head label="Window">
                  {['Visits', 'Clicks', 'Conv.'].map((h) => <Num key={h} head>{h}</Num>)}
                </Row>
                {WINDOWS.map(([key, label]) => (
                  <Row key={key} label={label}>
                    <Num>{fmt(activity[key]?.visits)}</Num>
                    <Num>{fmt(activity[key]?.clicks)}</Num>
                    <Num>{fmt(activity[key]?.conversions)}</Num>
                  </Row>
                ))}
              </Framed>
            </Box>

            {(age || relay) && (
              <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                <SectionTitle>Who converted</SectionTitle>
                <Framed cols="1fr 96px">
                  <Row head label="Metric"><Num head>Value</Num></Row>
                  {age && (
                    <>
                      <Row label="Average age"><Num>{age.avg}</Num></Row>
                      <Row label="Over 65"><Num>{age.over65Pct}%</Num></Row>
                      <Row label="Reported an age"><Num>{fmt(age.sampled)}</Num></Row>
                    </>
                  )}
                  {relay && (
                    <Row label="Reported to source">
                      <Num>
                        {Math.round((relay.sent / relay.total) * 100)}%
                        {' '}({fmt(relay.sent)}/{fmt(relay.total)})
                      </Num>
                    </Row>
                  )}
                </Framed>
                {relay?.sent === 0 && (
                  <Typography variant="caption" display="block" sx={{ opacity: 0.7, mt: 0.25 }}>
                    Expected where the network fires the pixel itself.
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {kind && entityId && (
            <>
              <Divider sx={{ my: 0.75, borderColor: 'divider' }} />
              <ActivityHourlyGraph kind={kind} entityId={entityId} open={open} />
              <Divider sx={{ my: 0.75, borderColor: 'divider' }} />
              <ActivityDistributions kind={kind} entityId={entityId} open={open} />
            </>
          )}

          <Divider sx={{ my: 0.75, borderColor: 'divider' }} />
          <Typography variant="caption" display="block" sx={{ opacity: 0.7 }}>
            {/* Last CHANGED, not last checked: the job skips rows whose figures did not move. */}
            {updatedAt && `Last changed ${formatDistanceToNow(new Date(updatedAt), { addSuffix: true })} · `}
            This entity everywhere it runs, not just this row. Days are UTC.
          </Typography>
        </Box>
        )}
      </Popover>
    </>
  );
}
